<template>
  <VSpinner v-if="!oldProduct" class="offset-top-16 offset-bottom-17" />
  <article v-else>
    <div id="PRODUCT_CHANGE_SELECTION">
      <div class="container-fixed">
        <div class="row">
          <div class="col-l-12">
            <!-- #53 -->
            <VProgress v-if="flags.progressBarEnabled" class="offset-top-2 offset-bottom-2" :steps="steps" active="substore.progress_selection" />
          </div>
        </div>
      </div>

      <section>
        <div class="container-fixed">
          <h3 class="marketing-l">
            <!-- #168 -->
            {{ $t('substore.your_current_product') }}
          </h3>

          <!-- #170, #171, #188 -->
          <VProductChangeDescription :product="oldProduct" property-suffix="Selection" class="offset-top-1 offset-bottom-1" />
        </div>

        <div class="grayed container-fluid">
          <div class="container-fixed">
            <div class="row">
              <div class="col-l-12 offset-top-1 offset-bottom-1">
                <!-- #189 -->
                <VPrice :price="oldProduct.price" :promotion-processing="false" :override-text-regular="oldProduct.texts.regularPriceOverrideText" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div class="container-fixed offset-top-2 offset-bottom-1">
          <h3 class="marketing-l">
            <!-- #169 -->
            {{ $t('substore.your_exchange_options') }}
          </h3>
        </div>

        <div v-for="newProduct in newProducts" :key="newProduct.subscriptionTemplateId" class="offset-bottom-2">
          <div class="container-fixed">
            <!-- #190, #191, #192 -->
            <VProductChangeDescription :product="newProduct" property-suffix="Selection" class="offset-top-1 offset-bottom-1" />
          </div>

          <div class="grayed container-fluid">
            <div class="container-fixed">
              <div class="row">
                <div class="col-l-9 col-s-12 offset-top-1 offset-bottom-1">
                  <VPriceDetails :price="newProduct.price" :promotion-type-optin="newProduct.promotionTypeOptin" :texts="newProduct.texts" />
                  <!-- #189 -->
                </div>
                <!-- #194, #253 -->
                <div class="col-l-3 col-s-12 offset-top-1 offset-bottom-1" style="text-align: right">
                  <span v-if="newProduct.canNotSucceed">
                    {{ $t('substore.editing_currently_not_possible') }}
                  </span>
                  <button
                    v-else
                    class="btn pull-right v-ctr"
                    :class="{ 'btn-white': newProduct !== selectedProduct, 'btn-brand': newProduct === selectedProduct }"
                    @click="btnSelect(newProduct)"
                  >
                    {{ $t(newProduct === selectedProduct ? 'substore.selected' : 'substore.select') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div class="container-fixed">
        <section class="offset-top-2 offset-right-1 button-bar" style="width: 100%">
          <!-- #195 -->
          <template v-if="newProducts.length > 0">{{ $t('substore.select_product_hint') }}</template>
          <template v-else>{{ $t('substore.select_product_no_options_hint') }}</template>
          <!-- #196 -->
          <div class="pull-right">
            <button type="button" class="btn btn-clean" @click="btnAbort">
              {{ $t('substore.abort') }}
            </button>

            <!-- #197 -->
            <button type="button" class="btn btn-default btn-brand" :disabled="selectedProduct === null" @click="btnNext">
              {{ $t('substore.next') }}
            </button>
          </div>
        </section>
      </div>
    </div>

    <div class="container-fixed offset-top-6">
      <VFootnotes :footnotes="footnotes" />
    </div>
  </article>
</template>

<script>
import Proxy from '@/proxies/SubstoreStockProxy';
import Transformer from '@/transformers/ProductChangeSelectionTransformer';
import Footnotes from '@/utils/Footnotes';
import TitleMixin from '@/mixins/title';
import OrderStepsMixin from '@/mixins/orderSteps';
import VSpinner from '@/components/Spinner';
import emitter from '@/plugins/emitter';
import { STOCK, STOCK_PRODUCT_CHANGE_SUMMARY } from '@route-types';

export default {
  name: 'ProductChangeSelection',

  components: {
    VSpinner,
    VProgress: () => import('@/components/Progress'),
    VPrice: () => import('@/components/Substore/Price'),
    VPriceDetails: () => import('@/components/Substore/PriceDetails'),
    VProductChangeDescription: () => import('@/components/Substore/StockProduct/ProductChangeDescription'),
    VFootnotes: () => import('@/components/UI/Footnotes'),
  },

  mixins: [TitleMixin, OrderStepsMixin],

  props: {
    subscriptionTemplateId: {
      type: String,
      required: true,
    },
    uniqueSubscriptionId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      oldProduct: null,
      newProducts: [],
      selectedProduct: null,
      flags: {
        progressBarEnabled: null,
      },
    };
  },

  async created() {
    const response = await new Proxy().stockProductChangeSelection(this.subscriptionTemplateId, this.uniqueSubscriptionId);

    if (response.head.fault !== null) throw new Error(response.head.fault);

    this.footnotes = new Footnotes();
    this.oldProduct = Transformer.fetch(response.data.oldProduct, this.footnotes);
    this.newProducts = response.data.newProducts.map(p => Transformer.fetch(p, this.footnotes));
    emitter.emit('setBannerPicture', this.oldProduct.images.headerImageChangeSelectionResourcePath);
    emitter.emit('setBannerHeadline', this.oldProduct.texts.headerTitleProductChangeSelection);
    emitter.emit('setBannerSubheadline', this.oldProduct.texts.headerSubTitleProductChangeSelection);

    Object.keys(this.flags).forEach(key => {
      this.flags[key] = response.data[key];
    });

    this.$webtrekk.viewPageWithPayload('produktwechsel', {
      page_type: 'basket',
      shop_product_sku: this.subscriptionTemplateId,
      shop_product_status: 'view',
      shop_product_name: this.oldProduct.texts?.productName,
    });
  },

  methods: {
    btnSelect(product) {
      this.selectedProduct = product;
    },
    btnAbort() {
      this.$router.push({ name: STOCK });
    },
    btnNext() {
      this.$router.push({
        name: STOCK_PRODUCT_CHANGE_SUMMARY,
        params: {
          subscriptionTemplateId: this.subscriptionTemplateId,
          uniqueSubscriptionId: this.uniqueSubscriptionId,
          newSubscriptionTemplateId: this.selectedProduct.subscriptionTemplateId,
        },
      });
    },
  },
};
</script>
